import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import Lenis from 'lenis';

export const usePageNavigation = () => {
  const reduced = useReducedMotion();
  const location = useLocation();
  const navigate = useNavigate();
  const lenis = useRef(null);
  const lastPath = useRef(null);

  const scrollToTarget = (target, immediate = false) => {
    const headerHeight = document.querySelector('.site-header')?.offsetHeight || 0;
    const top = target.id === 'home' ? 0 : window.scrollY + target.getBoundingClientRect().top - headerHeight - 16;
    const focus = () => { target.setAttribute('tabindex', '-1'); target.focus({ preventScroll: true }); };
    if (lenis.current) lenis.current.scrollTo(top, { immediate, onComplete: focus });
    else { window.scrollTo({ top, behavior: 'instant' }); focus(); }
  };

  useEffect(() => {
    if (!reduced) lenis.current = new Lenis({ autoRaf: true, duration: 1.05, anchors: false, prevent: node => node.hasAttribute('data-lenis-prevent') || node.getAttribute('role') === 'dialog' });
    return () => { lenis.current?.destroy(); lenis.current = null; };
  }, [reduced]);

  useEffect(() => {
    const pathChanged = lastPath.current !== location.pathname;
    lastPath.current = location.pathname;
    const frame = requestAnimationFrame(() => {
      lenis.current?.resize();
      let targetId = location.hash.slice(1);
      try { targetId = decodeURIComponent(targetId); } catch { /* Treat malformed fragments as an unknown anchor. */ }
      const target = targetId ? document.getElementById(targetId) : null;
      if (target) scrollToTarget(target, pathChanged || !!reduced);
      else if (pathChanged) {
        if (lenis.current) lenis.current.scrollTo(0, { immediate: true });
        else window.scrollTo({ top: 0, behavior: 'instant' });
        const main = document.getElementById('main-content');
        main?.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(frame);
    // The target is derived from the current URL after the route has rendered.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.hash, reduced]);

  useEffect(() => {
    const onAnchor = event => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const anchor = event.target.closest?.('a[href^="#"]');
      if (!anchor) return;
      const hash = anchor.getAttribute('href');
      const target = document.getElementById(hash.slice(1));
      if (!target) return;
      event.preventDefault();
      if (location.hash === hash) scrollToTarget(target, !!reduced);
      else navigate(hash, { preventScrollReset: true });
    };
    document.addEventListener('click', onAnchor);
    return () => document.removeEventListener('click', onAnchor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash, navigate, reduced]);
};